"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.usePasskey = void 0;
const react_1 = require("react");
/**
 * Hook for managing WebAuthn passkeys via Cognito Managed Login v2 endpoints.
 *
 * Prerequisites (AWS console — no code changes):
 *  - Cognito tier: ESSENTIALS or higher
 *  - Managed Login v2 enabled on the User Pool
 *  - WEB_AUTHN added to SignInPolicy.AllowedFirstAuthFactors
 */
const usePasskey = () => {
    const isPasskeySupported = typeof window !== 'undefined' && !!window.PublicKeyCredential;
    const [isLoading, setIsLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)(null);
    const [passkeys, setPasskeys] = (0, react_1.useState)([]);
    const registerPasskey = (0, react_1.useCallback)(async (accessToken, cognitoDomain) => {
        if (!isPasskeySupported) {
            setError('WebAuthn is not supported in this browser.');
            return;
        }
        setIsLoading(true);
        setError(null);
        try {
            const startRes = await fetch(`${cognitoDomain}/passkeys/register/start`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!startRes.ok)
                throw new Error(`Registration start failed: ${startRes.status}`);
            const options = await startRes.json();
            const credential = await navigator.credentials.create({
                publicKey: options,
            });
            if (!credential)
                throw new Error('WebAuthn ceremony cancelled or failed.');
            const completeRes = await fetch(`${cognitoDomain}/passkeys/register/complete`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(credential),
            });
            if (!completeRes.ok)
                throw new Error(`Registration complete failed: ${completeRes.status}`);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Passkey registration failed.');
        }
        finally {
            setIsLoading(false);
        }
    }, [isPasskeySupported]);
    const listPasskeys = (0, react_1.useCallback)(async (accessToken, cognitoDomain) => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await fetch(`${cognitoDomain}/passkeys`, {
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!res.ok)
                throw new Error(`List passkeys failed: ${res.status}`);
            const data = await res.json();
            setPasskeys(data?.credentials ?? []);
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to list passkeys.');
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    const deletePasskey = (0, react_1.useCallback)(async (accessToken, cognitoDomain, credentialId) => {
        setIsLoading(true);
        setError(null);
        try {
            const res = await fetch(`${cognitoDomain}/passkeys/${credentialId}`, {
                method: 'DELETE',
                headers: { Authorization: `Bearer ${accessToken}` },
            });
            if (!res.ok)
                throw new Error(`Delete passkey failed: ${res.status}`);
            setPasskeys((prev) => prev.filter((p) => p.credentialId !== credentialId));
        }
        catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to delete passkey.');
        }
        finally {
            setIsLoading(false);
        }
    }, []);
    return {
        isPasskeySupported,
        isLoading,
        error,
        passkeys,
        registerPasskey,
        listPasskeys,
        deletePasskey,
    };
};
exports.usePasskey = usePasskey;
